import { type ReactElement, useMemo } from 'react';
import { queryKeys } from '@/shared/hooks';
import { useAccessToken } from '@/shared/hooks/auth';
import { getStatusToneTextColor, Surface, Text } from '@/ui';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { formatDate } from '@/ui/formatters/formatDate';
import { type GridColDef } from '@/ui/primitives';
import { querySuperuserUsers } from '../data/superuserUsers';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { UserListItemDto } from '@flowqa/shared';

export const SuperuserUsersTable = (): ReactElement => {
  const accessToken = useAccessToken();
  const columns = useMemo<Array<GridColDef<UserListItemDto>>>(() => {
    const emailColumn: GridColDef<UserListItemDto> = {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      valueGetter: (_value, row) => row.email,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.email}
        </Text>
      ),
    };
    const createdAtColumn: GridColDef<UserListItemDto> = {
      field: 'createdAt',
      headerName: 'Signed Up',
      flex: 1,
      type: 'date',
      valueGetter: (_value, row) => {
        const raw = row.createdAt;
        if (raw instanceof Date) return raw;
        if (typeof raw === 'string') {
          const parsed = new Date(raw);
          return Number.isNaN(parsed.valueOf()) ? null : parsed;
        }
        return null;
      },
      renderCell: (params) => (
        <Text role="body" component="span">
          {formatDate(params.row.createdAt) || '-'}
        </Text>
      ),
    };
    const statusColumn: GridColDef<UserListItemDto> = {
      field: 'blockedAt',
      headerName: 'Status',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const label = row.blockedAt ? 'Blocked' : 'Active';
        const statusTone = row.blockedAt ? 'red' : 'green';
        return (
          <Text
            role="body"
            component="span"
            transform="uppercase"
            sx={{
              color: (theme) => getStatusToneTextColor(theme, statusTone),
            }}
          >
            {label}
          </Text>
        );
      },
    };

    return [emailColumn, createdAtColumn, statusColumn];
  }, []);

  const queryFn = async (
    input: GridQueryInput,
  ): Promise<{ items: UserListItemDto[]; total: number; page: number; pageSize: number }> => {
    return await querySuperuserUsers(input);
  };

  return (
    <>
      <Surface padding="none" border={false} sx={{ width: '100%', minWidth: 0 }}>
        <ServerDataTable<UserListItemDto>
          queryPath="superuser-users"
          queryKeyPrefix={queryKeys.superuserUsers()}
          queryFn={queryFn}
          columns={columns}
          getRowId={(row) => row.id}
          token={accessToken}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={5}
          autoHeight
          noRowsText="No users found"
          disableColumnMenu
        />
      </Surface>
    </>
  );
};
